/**
 * Created by fers on 26.09.15.
 */

import { v4 as uuidv4 } from 'uuid';
import { declareLinkedEntity, userSupportedTypes } from '../fw/MetaDataHelper.ts';
import * as Permission from '../util/Permission';
import { ACCESS_TYPE_DEVELOPER, ACCESS_TYPE_PARTNER1, ACCESS_TYPE_PARTNER2, ACCESS_TYPE_REGULARUSER } from './UserType';

export default declareLinkedEntity({
    entity: "formFields",
    graphQL: "FormField",
    name: "Form Field",
    writePermission: [
        Permission.PERM_EDIT_CHANNEL_DEF
    ],
    parentRelation: "formFields",
    defaultTitle: (data) => {
        return data['name'];
    },
    defaultOrder: ['order', 'name'],
    isPublic: true,
    dynamoPrefix: 'def',
    storageType: 'schema',
    onModify: ['flowDef'],
    fields: (types) => { return {
        id: {
            name: "Field ID",
            type: "id",
            defaultValue: () => {
                return uuidv4();
            },
            primaryKey: true,
            required: true,
            userEditable: false,
            compositeKey: true,
            isPublic: true,
        },
        name: {
            name: "Name",
            type: "string",
            defaultValue: "",
            translatable: true,
            isPublic: true,
        },
        defaultValue: {
            name: "Default Value",
            type: "string",
            translatable: true,
            trim: true,
            defaultValue: ""
        },
        order: {
            name: "Order",
            type: "int",
            defaultValue: "0",
        },
        required: {
            name: "Required",
            type: "boolean",
            defaultValue: true,
            visibility: (data) => { return !data['disabled']; }
        },
        readOnly: {
            name: "Read only",
            type: "boolean",
            defaultValue: false,
            visibility: (data) => { return !data['disabled']; }
        },
        exampleData: {
            name: "Example data",
            type: "string",
            translatable: true,
            defaultValue: "",
            description: "It is used for rendering filled boxes with data.",
            isPublic: true,
        },
        prefix: {
            name: "Prefix",
            type: "string",
            trim: true,
            translatable: true,
            description: "It is part of input field and suggest what user should add (eg. https://[user entered data].example.com/).",
            defaultValue: "",
            visibility: (data) => { return data['type'] !== 'form' && data['type'] !== 'enum' && data['type'] !== 'enums'  && data['type'] !== 'dictionary'; },
        },
        suffix: {
            name: "Suffix",
            type: "string",
            trim: true,
            translatable: true,
            description: "It is part of input field and suggest what user should add (eg. https://[user entered data].example.com/).",
            defaultValue: "",
            visibility: (data) => { return data['type'] !== 'form' && data['type'] !== 'enum' && data['type'] !== 'enums'  && data['type'] !== 'dictionary'; },
        },
        disabled: {
            name: "Disabled",
            type: "boolean",
            defaultValue: false,
        },
        key: {
            name: "Key",
            type: "string",
            defaultValue: "",
            required: true,
            trim: true,
            validations: {
                matchRegexp: /^[a-zA-Z0-9_]+$/,
            },
            isPublic: true,
        },
        type: {
            name: "Type",
            type: "enum",
            defaultValue: "string",
            values: {...userSupportedTypes,     "form": {
                    "name": "Form",
                }
            },
            isPublic: true,
        },
        description: {
            name: "Description",
            type: "text",
            translatable: true,
            defaultValue: "",
            isPublic: true,
        },
        styleAddon: {
            name: "Style addon (CSS)",
            type: "string",
            defaultValue: "",
        },
        regexp: {
            name: "RegExp Validation",
            description: "Validation must contain ^$. Example: ^[a-zA-Z0-9\-]+$",
            type: "string",
            defaultValue: "",
            visibility: (data) => { return data['type'] !== 'form'; }
        },
        fileContentTypes: {
            name: "File content types",
            type: "string",
            description: "File content type, separated by comma.",
            visibility: (data) => { return data['type'] === 'file' || data['type'] === 'certificate'; },
        },
        subForm: {
            name: "Form",
            to: () => types['forms'],
            type: "relation",
            relativeTo: ['flowDef'],
            visibility: (data) => { return data['type'] === 'form'; },
        },
        formStyle: {
            name: "Form Style",
            type: "enum",
            values: {
                "table": {
                    name: "Table form"
                },
                "vertical": {
                    name: "Vertical form"
                },
                "editable": {
                    name: "Editable form"
                },
            },
            visibility: (data) => { return data['type'] === 'form'; },
        },
        datiQuery: {
            name: "DatiQuery",
            type: "string",
            visibility: (data) => { return data['type'] === 'enum' || data['type'] === 'enums'; },
            descriptionHtml: "<small>Example: <pre>SELECT * FROM [APP $CURRENT TYPE \"eu.flexibee.c\" ADDING]</pre></small>",
        },
        datiQueryHasParameters: {
            name: "DatiQuery has parameters",
            type: "boolean",
            visibility: (data) => { return data['type'] === 'enum' || data['type'] === 'enums'; },
        },
        enumValues: {
            name: "Enum Values",
            type: "object",
            visibility: (data) => { return data['type'] === 'enum' || data['type'] === 'enums'; },
            descriptionHtml: "<small>Example: <pre>{    \"CZ\": {\n        \"name\": {\n            \"en\": \"Czech\",\n            \"cs\": \"Česko\"\n        }\n    },\n    \"EN\": {\n        \"name: {\n            \"en\": \"English\",\n            \"cs\": \"Česky\"\n        }\n    }}</pre></small>",
        },
        flowChannelDef: {
            name: "Application Definition",
            to: () => types['flowChannelDefs'],
            type: "relation",
            visibility: (data) => { return data['type'] === 'enum' || data['type'] === 'enums'; },
            relativeTo: ["flowDef"],
        },
        form: {
            name: "Form",
            to: () => types['forms'],
            type: "relation",
            defaultValue: (form) => {
                return form['id'];
            },
            parent: true,
            hiddenCreate: true,
            compositeKey: true,
            relativeTo: ['flowDef'],
        },
        visibilityJS: {
            name: "Visibility JS",
            type: "javascript",
            defaultValue: "",
            globalVariables: {
                'getValue': 'getValue(field: string)',
                'setTitle': 'setTitle(title: string)',
                'hasChannelDef': 'hasChannelDef(keyOrChannelDefId: string, alias: string, noExtended?: boolean)',
            },
        },
        filterValuesJS: {
            name: "Filter Values JS",
            type: "javascript",
            defaultValue: "",
            globalVariables: {
                'getValue': 'getValue(field: string)',
                'hasChannelDef': 'hasChannelDef(keyOrChannelDefId: string, alias: string, noExtended?: boolean)',
                'json': 'json: Map<String,String>',
            },
            visibility: (data) => { return data['type'] === 'enum' || data['type'] === 'enums' || data['type'] === 'dictionary'; },
        },
        flowDef: {
            name: "Flow Definition",
            to: () => types['flowDefs'],
            type: "relation",
            defaultValue: (form) => {
                return form['fieldDef_id'];
            },
            hiddenCreate: true,
            compositeKey: true,
        },
        flowDefVersion: {
            name: "Flow Definition Version",
            to: () => types['flowDefVersions'],
            type: "relation",
            defaultValue: (flow) => {
                return flow['flowDefVersion_id'];
            },
            required: true,
            hiddenCreate: true,
            compositeKey: true,
        },
        accessType: {
            name: "Access Type",
            type: "enum",
            visibility: (data) => { return !data['disabled']; },
            values: {
                [ACCESS_TYPE_REGULARUSER]: {
                    name: "Regular User",
                },
                [ACCESS_TYPE_PARTNER1]: {
                    name: "Partner 1",
                },
                [ACCESS_TYPE_PARTNER2]: {
                    name: "Partner 2",
                },
                [ACCESS_TYPE_DEVELOPER]: {
                    name: "Developers Only",
                },
            },
            defaultValue: ACCESS_TYPE_REGULARUSER,
        },
        developersOnly: {
            name: "Visible only for developers",
            type: "boolean",
            defaultValue: false,
        },
        partner1Only: {
            name: "Visible only for Partner 1",
            type: "boolean",
            defaultValue: false,
        },
        partner2Only: {
            name: "Visible only for Partner 2",
            type: "boolean",
            defaultValue: false,
        },
        author: {
            name: "Author",
            type: "relation",
            to: () => types['users'],
            userEditable: false,
        },
        updatedAt: {
            name: "Updated At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        createdAt: {
            name: "Created At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        }
    }; },
});
